/**
 * Vendor detail sub-components — ported from AJS directives.
 *
 * vendor-branch-invoices  → wor-table backed by /vendors/invoice_list
 * vendor-branch-payments  → wor-table backed by /vendors/branch_payment_list
 * vendor-orders           → wor-table backed by /vendors/purchase_order_list
 * vendor-invoices         → wor-table backed by /vendors/invoice_list
 * vendor-payments         → wor-table backed by /vendors/payment_list
 * vendor-billed           → wor-table backed by /vendors/biller_child_list
 */
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { ApiService }           from '../api/api.service';
import { TableQueryParameters } from '../table/types/table-query-parameters';
import { TableRowEvent }        from '../table/types/table-row-event';
import { TableService }         from '../table/table.service';
import { EditComponent }        from '../table/cell-components/actions/edit.component';
import { VendorModel }          from './models/vendor';

// ---------------------------------------------------------------------------
// vendor-branch-invoices
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-branch-invoices',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-folder-open"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.BRANCH_VENDOR_INVOICES' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="invoices"
          (query)="getInvoices($event.params)"
          [settings]="tableSettings">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorBranchInvoicesPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() invoices: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.invoices?.length;
    const cache = this.tableService.getCacheForTable('branchVendorInvoices');

    this.tableSettings = {
      cache: { enabled: true, key: 'branchVendorInvoices' },
      columns: [
        { field: 'vendor_invoice_number', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.REFERENCE'), classes: 'text-left' }, classes: 'text-left' },
        { field: 'post_date',             header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE'), classes: 'text-left' }, pipe: 'globalDate', classes: 'text-left' },
        { field: 'invoice_type',          header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE'), classes: 'text-left' }, pipe: 'invoiceType', classes: 'text-left' },
        { field: 'invoice_status',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS'), classes: 'text-left' }, pipe: 'invoiceStatus', classes: 'text-left' },
        { field: 'total',                 header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'balance_due',           header: { text: this.translate.instant('JS_SPACE.COLUMNS.BALANCE'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vendors/invoice_count', [this.vendor.id, '*', '*', '*', '*', '*']) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getInvoices(params: TableQueryParameters) {
    return this.api.get('/vendors/invoice_list', [this.vendor.id, params.offset, params.pageSize, '*', '*', '*', '*', '*', params.field, params.direction])
      .then((invoices: Record<string, unknown>[]) => { this.invoices = invoices; return invoices; });
  }
}

// ---------------------------------------------------------------------------
// vendor-branch-payments
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-branch-payments',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-money"></i><span class="break"></span>{{ 'Branch Payments' }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="payments"
          (query)="getPayments($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorBranchPaymentsPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() payments: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.payments?.length;
    const cache = this.tableService.getCacheForTable('vendorBranchPayments');

    this.tableSettings = {
      cache: { enabled: true, key: 'vendorBranchPayments' },
      columns: [
        { field: 'branch_vendor_payment_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.PAYMENT_NUMBER') }, classes: 'text-left' },
        { field: 'post_date',                    header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate', classes: 'text-left' },
        { field: 'status',                       header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'customerPaymentStatus', classes: 'text-left' },
        { field: 'applied_amount',               header: { text: this.translate.instant('JS_SPACE.COLUMNS.APPLIED_AMOUNT') }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'amount',                       header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT') }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string; vendor_id: string }) => this.viewPayment(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PAYMENTS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/branch_vendor_payments/count') },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getPayments(params: TableQueryParameters) {
    return this.api.get('/vendors/branch_payment_list', [this.vendor.id, params.offset, params.pageSize, params.field, params.direction])
      .then((payments: Record<string, unknown>[]) => { this.payments = payments; return payments; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewPayment(event.row);
  }

  viewPayment(payment: { id: string; vendor_id: string }) {
    this.router.stateService.go('app.vendor-branch-payment', { vendor_id: payment.vendor_id, id: payment.id });
  }
}

// ---------------------------------------------------------------------------
// vendor-orders
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-orders',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-list-ul"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.STOCK_ORDERS' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="orders"
          (query)="getOrders($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorOrdersPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() orders: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.orders?.length;
    const cache = this.tableService.getCacheForTable('vendorOrders');

    this.tableSettings = {
      cache: { enabled: true, key: 'vendorOrders' },
      columns: [
        { field: 'order_number',  header: { text: this.translate.instant('PURCHASE_ORDER_SPACE.ORDER_NUMBER') } },
        { field: 'order_date',    header: { text: this.translate.instant('GENERAL_SPACE.FIELD.DATE') }, pipe: 'globalDate' },
        { field: 'due_date',      header: { text: this.translate.instant('PURCHASE_ORDER_SPACE.DUE_DATE') }, pipe: 'globalDate' },
        { field: 'note',          header: { text: this.translate.instant('INSPECTIONS_SPACE.COMMENT') } },
        { field: 'status',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'purchaseOrderStatus' },
        { field: 'total',         header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string; vendor_id: string }) => this.viewOrder(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_STOCK_ORDERS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vendors/purchase_order_count', [this.vendor.id, '*']) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'order_date' } };
  }

  getOrders(params: TableQueryParameters) {
    return this.api.get('/vendors/purchase_order_list', [this.vendor.id, params.offset, params.pageSize, '*', params.field, params.direction])
      .then((orders: Record<string, unknown>[]) => { this.orders = orders; return orders; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewOrder(event.row);
  }

  viewOrder(order: { id: string; vendor_id: string }) {
    this.router.stateService.go('app.purchase-order', { vendor_id: order.vendor_id, id: order.id });
  }
}

// ---------------------------------------------------------------------------
// vendor-invoices
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-invoices',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-folder-open"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.VENDOR_INVOICES' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="invoices"
          (query)="getInvoices($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorInvoicesPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() invoices: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.invoices?.length;
    const cache = this.tableService.getCacheForTable('vendorInvoices');

    this.tableSettings = {
      cache: { enabled: true, key: 'vendorInvoices' },
      columns: [
        { field: 'vendor_invoice_number', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.REFERENCE') } },
        { field: 'post_date',             header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'invoice_type',          header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE') }, pipe: 'invoiceType' },
        { field: 'invoice_status',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'invoiceStatus' },
        { field: 'total',                 header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'balance_due',           header: { text: this.translate.instant('JS_SPACE.COLUMNS.BALANCE'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string }) => this.viewInvoice(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vendors/invoice_count', [this.vendor.id, '*', '*', '*', '*', '*']) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getInvoices(params: TableQueryParameters) {
    return this.api.get('/vendors/invoice_list', [this.vendor.id, params.offset, params.pageSize, '*', '*', '*', '*', '*', params.field, params.direction])
      .then((invoices: Record<string, unknown>[]) => { this.invoices = invoices; return invoices; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewInvoice(event.row);
  }

  viewInvoice(invoice: { id: string }) {
    this.router.stateService.go('app.vendor-invoice', { vendor_id: this.vendor.id, id: invoice.id });
  }
}

// ---------------------------------------------------------------------------
// vendor-payments
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-payments',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-money"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.VENDOR_PAYMENTS' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="payments"
          (query)="getPayments($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorPaymentsPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() payments: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.payments?.length;
    const cache = this.tableService.getCacheForTable('vendorPayments');

    this.tableSettings = {
      cache: { enabled: true, key: 'vendorPayments' },
      columns: [
        { field: 'vendor_payment_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.PAYMENT_NUMBER') } },
        { field: 'post_date',             header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'status',                header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'customerPaymentStatus' },
        { field: 'applied_amount',        header: { text: this.translate.instant('JS_SPACE.COLUMNS.APPLIED_AMOUNT') }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'amount',                header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT') }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string; vendor_id: string }) => this.viewPayment(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PAYMENTS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vendors/payment_count/' + this.vendor.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'asc', field: cache.sorting?.field || 'post_date' } };
  }

  getPayments(params: TableQueryParameters) {
    return this.api.get('/vendors/payment_list', [this.vendor.id, params.offset, params.pageSize, params.field, params.direction])
      .then((payments: Record<string, unknown>[]) => { this.payments = payments; return payments; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewPayment(event.row);
  }

  viewPayment(payment: { id: string; vendor_id: string }) {
    this.router.stateService.go('app.vendor-payment', { vendor_id: payment.vendor_id, id: payment.id });
  }
}

// ---------------------------------------------------------------------------
// vendor-billed
// ---------------------------------------------------------------------------
@Component({
  selector: 'vendor-billed',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-users"></i><span class="break"></span>{{ 'Billed Vendors' }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="vendors"
          (query)="getVendors($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VendorBilledPlaceholderComponent implements OnInit {
  @Input() vendor: VendorModel;
  @Input() vendors: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.vendors?.length;
    const cache = this.tableService.getCacheForTable('vendorBilled');

    this.tableSettings = {
      cache: { enabled: true, key: 'vendorBilled' },
      columns: [
        { field: 'company_name', header: { text: this.translate.instant('COMPANY_SPACE.COMPANY') }, classes: 'all-caps' },
        { field: 'phone',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.PHONE') } },
        { field: 'email',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.EMAIL') } },
        { component: { bindings: { outputs: { onEditClick: (e: { id: string }) => this.viewVendor(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_SUPPLIERS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vendors/biller_child_count/' + this.vendor.id) },
      rows: { highlight: { enabled: true } },
      sorting: { enabled: false } };
  }

  getVendors(params: TableQueryParameters) {
    return this.api.get('/vendors/biller_child_list', [this.vendor.id, params.offset, params.pageSize])
      .then((vendors: Record<string, unknown>[]) => { this.vendors = vendors; return vendors; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewVendor(event.row);
  }

  viewVendor(vendor: { id: string }) {
    this.router.stateService.go('app.vendor', { id: vendor.id });
  }
}
